import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getContractsRequest } from "Api/billingSystem";

import type { RootState } from "Store/configureStore";
import type { ContractsResponse, Contract } from "Api/billingSystem/types";

// Thunks
export const getContracts = createAppAsyncThunk(
  "billingSystem/contracts/getByProfile",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getContractsRequest(billingProfileId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type ContractsState = {
  byProfileId: Record<
    string,
    {
      loading: boolean;
      data?: ContractsResponse;
      error?: string;
    }
  >;
};

const initialState: ContractsState = {
  byProfileId: {}
};

// Slice
const contractsSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getContracts.pending, (state, { meta }) => {
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getContracts.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "data"],
          action.payload
        );
      })
      .addCase(getContracts.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "error"],
          action.payload?.error
        );
      });
  }
});

export default contractsSlice.reducer;

// Selectors
const contractsStateSelector = (state: RootState) => state.contracts;

export const selectProfileContracts = createSelector(
  contractsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.data
);

export const selectProfileContractsLoading = createSelector(
  contractsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) =>
    state.byProfileId[billingProfileId]?.loading ?? false
);

export const selectProfileContractsError = createSelector(
  contractsStateSelector,
  (_: RootState, billingProfileId: string) => billingProfileId,
  (state, billingProfileId) => state.byProfileId[billingProfileId]?.error
);

export const selectProfileContractsList = createSelector(
  selectProfileContracts,
  data => data?.contracts
);

export const selectProfileActiveCommitments = createSelector(
  selectProfileContractsList,
  (contracts): Contract[] | undefined =>
    contracts?.filter(
      contract => contract.type === "commitment" && contract.status === "active"
    )
);

export const selectProfileHasActiveCommitments = createSelector(
  selectProfileActiveCommitments,
  data => (data ?? []).length > 0
);
